import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL || "";
const MAX_RETRIES = 2;

// Returns true if this error is a transient network failure worth retrying.
// Never retry on 4xx (client errors) or 401 (auth).
function isRetryable(err) {
  if (!err.response) return true; // network error / timeout
  const s = err.response.status;
  return s >= 500 && s !== 501; // 5xx except Not Implemented
}

function withRetry(axiosInstance) {
  axiosInstance.interceptors.response.use(null, async err => {
    const config = err.config;
    if (!config) return Promise.reject(err);

    config.__retryCount = config.__retryCount || 0;
    if (config.__retryCount >= MAX_RETRIES || !isRetryable(err)) {
      return Promise.reject(err);
    }
    config.__retryCount += 1;
    const delay = config.__retryCount * 800; // 800ms, 1600ms
    await new Promise(r => setTimeout(r, delay));
    return axiosInstance(config);
  });
}

// Unauthenticated client for guest share endpoints — no auth interceptor, no redirect on 401.
export const guestApi = axios.create({ baseURL: BASE_URL, timeout: 15000 });
withRetry(guestApi);

const api = axios.create({ baseURL: BASE_URL, timeout: 15000 });

// Attach JWT to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem("trackin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Retry transient failures before applying auth/quota logic
withRetry(api);

// On 401 — clear session and redirect to login
// On 402/403 — fire a quota-sync event so AuthContext refreshes from DB
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem("trackin_token");
      localStorage.removeItem("trackin_org");
      window.location.href = "/auth";
    }
    if (err.response?.status === 402 || err.response?.status === 403) {
      window.dispatchEvent(new CustomEvent("trackin:quota-sync"));
    }
    const msg = err.response?.data?.error || err.message || "Request failed";
    const error = new Error(msg);
    error.status = err.response?.status;
    error.quotaExhausted = err.response?.status === 402;
    error.suspended      = err.response?.data?.suspended === true;
    return Promise.reject(error);
  }
);

export default api;
